import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const MobileTestingMigrationBanner = ({ onDismiss }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const dismissed = localStorage.getItem('scandium_migration_banner_dismissed');
    if (dismissed === 'true') {
      setIsVisible(false);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('scandium_migration_banner_dismissed', 'true');
    if (onDismiss) onDismiss();
  };

  const handleNavigateToRova = () => {
    window.open('https://mobile.rova.qa', '_blank');
  };

  if (!isVisible) return null;

  const isDark = theme.palette.mode === 'dark';
  
  const bannerColors = {
    background: isDark 
      ? theme.palette.warning.dark 
      : '#FFF8E1',
    text: isDark 
      ? '#FFFFFF' 
      : '#4A3000', 
    link: isDark 
      ? '#FFE082' 
      : '#7B5800', 
    border: isDark 
      ? theme.palette.warning.main 
      : '#FFE082',
    badgeBackground: isDark 
      ? 'rgba(255,255,255,0.15)' 
      : 'rgba(75, 48, 0, 0.08)',
    buttonBackground: isDark 
      ? 'rgba(255,255,255,0.9)' 
      : '#4A3000',
    buttonText: isDark 
      ? theme.palette.warning.dark 
      : '#FFFFFF',
    buttonHover: isDark 
      ? '#FFFFFF' 
      : '#5C3D00',
    iconColor: isDark 
      ? '#FFFFFF' 
      : '#4A3000'
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        // zIndex: 1200,
        width: '100%',
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 1, sm: 1.5 },
        backgroundColor: bannerColors.background,
        borderBottom: `1px solid ${bannerColors.border}`,
        boxShadow: theme.shadows[2],
        transition: 'all 0.3s ease'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          maxWidth: '1400px',
          margin: '0 auto',
          gap: { xs: 1, sm: 2 }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1.5,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <InfoOutlinedIcon 
            sx={{ 
              color: bannerColors.iconColor,
              fontSize: { xs: 20, sm: 24 },
              mt: { xs: 0.5, sm: 0 }
            }} 
          />
          
          <Box>
            <Typography
              variant={isSmallScreen ? 'body2' : 'body1'}
              sx={{
                fontWeight: 600,
                color: bannerColors.text,
                lineHeight: 1.2
              }}
            >
              Important Update: Mobile Emulator Discontinuation
            </Typography>
            
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 0.5, sm: 1 },
                mt: 0.5,
                flexWrap: 'wrap'
              }}
            >
              <Typography
                variant={isSmallScreen ? 'caption' : 'body2'}
                sx={{
                  color: bannerColors.text,
                  opacity: 0.9
                }}
              >
                Mobile testing has moved to{' '}
                <Link
                  href="https://mobile.rova.qa"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: bannerColors.link,
                    fontWeight: 700,
                    textDecoration: 'underline',
                    '&:hover': {
                      opacity: 0.8,
                      textDecoration: 'none'
                    }
                  }}
                >
                  ROVA AI Mobile
                </Link>
                {' '}- the enhanced platform for web & mobile testing.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            flexWrap: 'wrap',
            mt: { xs: 0.5, sm: 0 }
          }}
        >
          <Button
            variant="contained"
            size={isSmallScreen ? 'small' : 'medium'}
            onClick={handleNavigateToRova}
            endIcon={<OpenInNewIcon />}
            sx={{
              backgroundColor: bannerColors.buttonBackground,
              color: bannerColors.buttonText,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: bannerColors.buttonHover
              },
              '& .MuiButton-endIcon': {
                color: bannerColors.buttonText
              }
            }}
          >
            Go to ROVA Mobile
          </Button>

          {/* <Button
            variant="text"
            size={isSmallScreen ? 'small' : 'medium'}
            sx={{
              color: bannerColors.text,
              fontWeight: 500,
              '&:hover': {
                backgroundColor: bannerColors.badgeBackground
              }
            }}
            onClick={() => {
              // Show more info or open documentation
              window.open('https://docs.rova.qa/migration', '_blank');
            }}
          >
            Learn More
          </Button> */}

          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              color: bannerColors.text,
              '&:hover': {
                backgroundColor: bannerColors.badgeBackground
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileTestingMigrationBanner;